import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import {
    COMMENT_SCOPE,
    COMMENT_SCOPE_ORDER,
    commentSlotIdentityForScope,
    getCommentSlotIdentityKey,
    getRelatedAssignmentsTableRows,
    getDefaultCommentScope,
    getPersonCommentForScope,
    getShiftName,
} from "@/lib/shifts";
import { classNames, dateFromCalendarISODateString, niceDate } from "@/lib/util";
import ConfirmationModal from "@/modules/shared/ui/ConfirmationModal";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Tooltip from "@/modules/shared/ui/Tooltip";

import type {
    SelectedPersonShiftLine,
    PersonAssignment,
    PersonShiftLineSlot,
    CommentSlotWithExistingId,
    CommentScope,
    GetPersonCommentsFn,
    RelatedAssignmentsTableRow,
} from "@/lib/shifts";

import "./ShiftBoardCommentModal.scss";

// =====================================================================================================================
// ShiftBoardCommentSlotStatus
// =====================================================================================================================

type ShiftBoardCommentSlotStatusProps = {
    isChecked: boolean;
    /** Text of an existing comment at this slot; omit when none. */
    existingCommentText?: string;
};

const ShiftBoardCommentSlotStatus = ({ isChecked, existingCommentText }: ShiftBoardCommentSlotStatusProps) => (
    <td data-column="status" className="shift-board-comment-slot-status text-center">
        {isChecked && existingCommentText !== undefined ? (
            <Tooltip
                interactive
                content={
                    <div className="shift-board-comment-slot-status__tooltip">
                        <p>
                            {translate(
                                "ShiftBoardCommentModal.existing_comment_will_be_overridden",
                                "Existing comment will be overridden"
                            )}
                        </p>
                        <div>{existingCommentText}</div>
                    </div>
                }
            >
                <i
                    className="fas fa-exclamation-triangle text-warning"
                    aria-label={translate("common.status", "Status")}
                />
            </Tooltip>
        ) : isChecked ? (
            <i className="fas fa-check-circle text-success" />
        ) : null}
    </td>
);

// =====================================================================================================================
// ShiftBoardCommentSlotSelector
// =====================================================================================================================

type ShiftBoardCommentSlotSelectorProps = {
    anchorSlot: PersonShiftLineSlot | null;
    rows: RelatedAssignmentsTableRow[];
    selectedSlotKeys: Set<string>;
    personId: number;
    getPersonComments: GetPersonCommentsFn;
    onToggleSlot: (key: string) => void;
    onToggleAll: (selectAll: boolean) => void;
};

const ShiftBoardCommentSlotSelector = ({
    anchorSlot,
    rows,
    selectedSlotKeys,
    personId,
    getPersonComments,
    onToggleSlot,
    onToggleAll,
}: ShiftBoardCommentSlotSelectorProps) => {
    const rowKeys = useMemo(() => rows.map(row => getCommentSlotIdentityKey(row.slot)), [rows]);

    const rowCount = rows.length;

    const selectedOtherCount = useMemo(
        () => rowKeys.filter(key => selectedSlotKeys.has(key)).length,
        [rowKeys, selectedSlotKeys]
    );

    const { isAllSelected, isSomeSelected } = useMemo(() => {
        return {
            isAllSelected: selectedOtherCount === rowCount && rowCount > 0,
            isSomeSelected: selectedOtherCount > 0 && selectedOtherCount < rowCount,
        };
    }, [selectedOtherCount, rowCount]);

    const handleSelectAllChange = () => {
        onToggleAll(!isAllSelected);
    };

    if (anchorSlot === null || rowCount === 0) {
        return null;
    }

    const allLabel = translate("common.all", "All");

    return (
        <details className="shift-board-comment-slot-selector">
            <summary className="shift-board-comment-slot-selector__summary">
                <span className="flex-grow-1">
                    {translate("ShiftBoardCommentModal.also_apply_to", "Also apply to")}...{" "}
                </span>
                <span className="badge badge-pill badge-secondary" title={translate("common.selected", "Selected")}>
                    {selectedOtherCount} / {rowCount}
                </span>
            </summary>
            <div className="shift-board-comment-slot-selector__content">
                <table className="table table-sm table-hover shift-board-comment-slot-selector__table">
                    <thead>
                        <tr>
                            <th data-column="checkbox">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={input => {
                                        if (input) {
                                            input.indeterminate = isSomeSelected;
                                        }
                                    }}
                                    onChange={handleSelectAllChange}
                                    aria-label={translate("common.select_all", "Select all")}
                                />
                            </th>
                            <th data-column="date">{translate("common.date", "Date")}</th>
                            <th data-column="shift">{translate("common.shift", "Shift")}</th>
                            <th data-column="line">{translate("common.line", "Line")}</th>
                            <th data-column="status" className="text-center">
                                <i className="fas fa-info-circle" title={translate("common.status", "Status")} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => {
                            const rowKey = getCommentSlotIdentityKey(row.slot);
                            const { slot, lineTitle } = row;
                            const existingCommentText = getPersonComments(
                                personId,
                                slot.date,
                                slot.shift,
                                slot.lineId
                            )[0]?.comment;

                            return (
                                <tr
                                    key={rowKey}
                                    className={selectedSlotKeys.has(rowKey) ? "table-active" : ""}
                                    onClick={() => onToggleSlot(rowKey)}
                                >
                                    <td data-column="checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedSlotKeys.has(rowKey)}
                                            onChange={() => onToggleSlot(rowKey)}
                                            onClick={(event: React.MouseEvent<HTMLInputElement>) =>
                                                event.stopPropagation()
                                            }
                                        />
                                    </td>
                                    <td data-column="date">{niceDate(dateFromCalendarISODateString(slot.date))}</td>
                                    <td data-column="shift">
                                        {slot.shift === null ? (
                                            <span className="text-muted">{allLabel}</span>
                                        ) : (
                                            translate(getShiftName(slot.shift))
                                        )}
                                    </td>
                                    <td data-column="line">
                                        {lineTitle === null ? (
                                            <span className="text-muted">{allLabel}</span>
                                        ) : (
                                            lineTitle
                                        )}
                                    </td>
                                    <ShiftBoardCommentSlotStatus
                                        isChecked={selectedSlotKeys.has(rowKey)}
                                        existingCommentText={existingCommentText}
                                    />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </details>
    );
};

// =====================================================================================================================
// ShiftBoardCommentModal
// =====================================================================================================================

/** Discriminant for unsaved-changes confirmation (modal close vs scope switch). */
const PENDING_DISCARD_KIND = {
    close: "close",
    scope: "scope",
} as const;

type PendingDiscardClose = { kind: typeof PENDING_DISCARD_KIND.close };
type PendingDiscardScope = { kind: typeof PENDING_DISCARD_KIND.scope; scope: CommentScope };
type PendingDiscard = PendingDiscardClose | PendingDiscardScope;

export type ShiftBoardCommentModalProps = {
    selectedPersonShiftLine: SelectedPersonShiftLine | null;
    relatedAssignments: PersonAssignment[];
    getPersonComments: GetPersonCommentsFn;
    show: boolean;
    onClose: () => void;
    onApply: (comment: string, personId: number, slots: CommentSlotWithExistingId[]) => void | Promise<void>;
    saveError?: string;
    isSaving?: boolean;
};

export const ShiftBoardCommentModal = ({
    selectedPersonShiftLine,
    relatedAssignments,
    getPersonComments,
    show,
    onClose,
    onApply,
    saveError = "",
    isSaving = false,
}: ShiftBoardCommentModalProps) => {
    const {
        lineTitle = "",
        personName = "",
        date: selectedDate = "",
        shift: selectedShift = 0,
    } = selectedPersonShiftLine || {};
    const dateLabel = selectedDate ? niceDate(dateFromCalendarISODateString(selectedDate)) : "";
    const shiftName = translate(getShiftName(selectedShift));

    const [commentValue, setCommentValue] = useState<string>("");
    const [savedCommentBaseline, setSavedCommentBaseline] = useState<string>("");
    const [selectedScope, setSelectedScope] = useState<CommentScope>(COMMENT_SCOPE.date);
    const [showClearConfirmation, setShowClearConfirmation] = useState<boolean>(false);
    const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(null);
    const [selectedSlotKeys, setSelectedSlotKeys] = useState<Set<string>>(new Set());
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const isCommentEmpty = commentValue === "";
    const isCommentDirty = commentValue !== savedCommentBaseline;
    const areSlotsSelected = selectedSlotKeys.size > 0;
    const isFormDirty = isCommentDirty || areSlotsSelected;

    useEffect(() => {
        if (!show || !selectedPersonShiftLine) {
            return;
        }

        const { personId, date, shift, lineId } = selectedPersonShiftLine;
        const slot: PersonShiftLineSlot = { date, shift, lineId };
        const defaultScope = getDefaultCommentScope(personId, slot, getPersonComments);
        const commentDetails = getPersonCommentForScope(personId, defaultScope, slot, getPersonComments);
        const comment = commentDetails?.comment ?? "";
        setSelectedScope(defaultScope);
        setCommentValue(comment);
        setSavedCommentBaseline(comment);
        setSelectedSlotKeys(new Set());

        setTimeout(() => textareaRef.current?.select(), 0); // Auto-select the text
    }, [show, selectedPersonShiftLine, getPersonComments]);

    useEffect(() => {
        if (!show) {
            setPendingDiscard(null);
        }
    }, [show]);

    const relatedAssignmentsTableRows = useMemo((): RelatedAssignmentsTableRow[] => {
        if (!selectedPersonShiftLine) {
            return [];
        }

        return getRelatedAssignmentsTableRows(relatedAssignments, selectedPersonShiftLine, selectedScope);
    }, [relatedAssignments, selectedPersonShiftLine, selectedScope]);

    const applyScopeChange = (scope: CommentScope) => {
        setSelectedScope(scope);
        setSelectedSlotKeys(new Set());
        if (!selectedPersonShiftLine) {
            return;
        }

        const { personId, date, shift, lineId } = selectedPersonShiftLine;
        const slot = { date, shift, lineId };
        const commentDetails = getPersonCommentForScope(personId, scope, slot, getPersonComments);
        const comment = commentDetails?.comment ?? "";
        setCommentValue(comment);
        setSavedCommentBaseline(comment);
    };

    const buildMergedSlots = (): CommentSlotWithExistingId[] => {
        if (!selectedPersonShiftLine) {
            return [];
        }

        const currentIdentity = commentSlotIdentityForScope(selectedPersonShiftLine, selectedScope);
        const currentComment = getPersonCommentForScope(
            selectedPersonShiftLine.personId,
            selectedScope,
            selectedPersonShiftLine,
            getPersonComments
        );
        const currentSlot: CommentSlotWithExistingId = {
            ...currentIdentity,
            existingId: currentComment?.id,
        };
        const pid = selectedPersonShiftLine.personId;
        const otherSlots: CommentSlotWithExistingId[] = relatedAssignmentsTableRows
            .filter(row => selectedSlotKeys.has(getCommentSlotIdentityKey(row.slot)))
            .map(row => ({
                ...row.slot,
                existingId: getPersonComments(pid, row.slot.date, row.slot.shift, row.slot.lineId)[0]?.id,
            }));

        return [currentSlot, ...otherSlots];
    };

    const handleScopeChange = (scope: CommentScope) => {
        if (scope === selectedScope) {
            return;
        }

        if (!isCommentDirty) {
            applyScopeChange(scope);
            return;
        }

        const pendingScope: PendingDiscardScope = { kind: PENDING_DISCARD_KIND.scope, scope };
        setPendingDiscard(pendingScope);
    };

    const requestClose = () => {
        if (pendingDiscard !== null) {
            setPendingDiscard(null);
            return;
        }

        if (showClearConfirmation) {
            setShowClearConfirmation(false);
            return;
        }

        if (!isCommentDirty) {
            onClose();
            return;
        }

        setPendingDiscard({ kind: PENDING_DISCARD_KIND.close });
    };

    const handleDiscardUnsavedConfirm = () => {
        const pending = pendingDiscard;
        setPendingDiscard(null);
        if (pending === null) {
            return;
        }

        if (pending.kind === PENDING_DISCARD_KIND.close) {
            onClose();
            return;
        }

        applyScopeChange(pending.scope);
    };

    const handleDiscardUnsavedCancel = () => {
        setPendingDiscard(null);
    };

    const handleApply = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedPersonShiftLine) {
            return;
        }
        const slots = buildMergedSlots();
        if (slots.length === 0) {
            return;
        }

        await onApply(commentValue, selectedPersonShiftLine.personId, slots);
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCommentValue(event.currentTarget.value);
    };

    const handleToggleSlot = (key: string) => {
        setSelectedSlotKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            return next;
        });
    };

    const handleToggleAll = (selectAll: boolean) => {
        if (!selectAll || !selectedPersonShiftLine) {
            setSelectedSlotKeys(new Set());
            return;
        }

        setSelectedSlotKeys(new Set(relatedAssignmentsTableRows.map(row => getCommentSlotIdentityKey(row.slot))));
    };

    const handleClearClick = () => {
        setShowClearConfirmation(true);
    };

    const handleClearConfirm = async () => {
        setShowClearConfirmation(false);
        if (!selectedPersonShiftLine) {
            return;
        }

        const slots = buildMergedSlots();
        if (slots.length === 0) {
            return;
        }

        await onApply("", selectedPersonShiftLine.personId, slots);
    };

    const handleClearCancel = () => {
        setShowClearConfirmation(false);
    };

    return (
        <>
            <Modal className="shift-board-comment-modal" show={show} onHide={requestClose} animation={false}>
                <Modal.Header>
                    <Modal.Title>{translate("common.edit_comment", "Edit comment")}</Modal.Title>
                    <button type="button" className="close" onClick={requestClose}>
                        <span>×</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    {saveError && <ErrorComponent className="mt-0" type="error" msg={saveError} />}
                    <form id="shift-board-comment-form" onSubmit={handleApply}>
                        <fieldset className="shift-board-comment-modal__scope form-group">
                            <legend className="sr-only">{translate("common.scope", "Scope")}</legend>
                            <div className="shift-board-comment-modal__scope-content">
                                <span
                                    className="shift-board-comment-modal__scope-radios btn-group btn-group-toggle"
                                    role="group"
                                    aria-label={translate("common.scope", "Scope")}
                                >
                                    {COMMENT_SCOPE_ORDER.map(scope => (
                                        <label
                                            className={classNames("btn btn-outline-secondary", {
                                                active: selectedScope === scope,
                                            })}
                                            key={scope}
                                        >
                                            <input
                                                type="radio"
                                                name="comment-scope"
                                                checked={selectedScope === scope}
                                                onChange={() => handleScopeChange(scope)}
                                            />
                                            {translate(`common.${scope}`)}
                                        </label>
                                    ))}
                                </span>
                                <dl className="shift-board-comment-modal__scope-details">
                                    <dt>{translate("common.person", "Person")}</dt>
                                    <dd>{personName}</dd>
                                    <dt>{translate("common.date", "Date")}</dt>
                                    <dd>{dateLabel}</dd>
                                    <dt>{translate("common.shift", "Shift")}</dt>
                                    <dd>
                                        {selectedScope !== COMMENT_SCOPE.date ? (
                                            shiftName
                                        ) : (
                                            <span className="text-muted">{translate("common.all", "All")}</span>
                                        )}
                                    </dd>
                                    <dt>{translate("common.line", "Line")}</dt>
                                    <dd>
                                        {selectedScope === COMMENT_SCOPE.line ? (
                                            lineTitle
                                        ) : (
                                            <span className="text-muted">{translate("common.all", "All")}</span>
                                        )}
                                    </dd>
                                </dl>
                            </div>
                            <p className="shift-board-comment-modal__scope-hint px-2 mx-1 mt-1 mb-0 text-muted">
                                <small>
                                    <i className="fas fa-info-circle mr-1" />
                                    {translate(`ShiftBoardCommentModal.comment_scope.${selectedScope}.description`)}
                                </small>
                            </p>
                        </fieldset>

                        <div className="shift-board-comment-modal__comment-field form-group mb-0">
                            <label htmlFor="shift-board-comment-control">
                                {translate("common.comment", "Comment")}
                            </label>
                            <textarea
                                ref={textareaRef}
                                id="shift-board-comment-control"
                                className="shift-board-comment-modal__comment-control form-control"
                                rows={5}
                                value={commentValue}
                                placeholder={translate("common.add_comment", "Add comment")}
                                onChange={handleChange}
                                autoFocus
                                disabled={isSaving}
                            />
                        </div>
                        <ShiftBoardCommentSlotSelector
                            anchorSlot={
                                selectedPersonShiftLine
                                    ? {
                                          date: selectedPersonShiftLine.date,
                                          shift: selectedPersonShiftLine.shift,
                                          lineId: selectedPersonShiftLine.lineId,
                                      }
                                    : null
                            }
                            rows={relatedAssignmentsTableRows}
                            selectedSlotKeys={selectedSlotKeys}
                            personId={selectedPersonShiftLine?.personId ?? 0}
                            getPersonComments={getPersonComments}
                            onToggleSlot={handleToggleSlot}
                            onToggleAll={handleToggleAll}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={requestClose}
                        disabled={isSaving}
                    >
                        {translate("common.cancel", "Cancel")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleClearClick}
                        disabled={isSaving || (!areSlotsSelected && isCommentEmpty)}
                    >
                        {translate("common.clear", "Clear")}
                    </button>
                    <button
                        type="submit"
                        form="shift-board-comment-form"
                        className="btn btn-primary"
                        disabled={isSaving || !isFormDirty}
                    >
                        {translate("common.save", "Save")} {isSaving && <i className="fas fa-spinner fa-spin ml-1" />}
                    </button>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                show={showClearConfirmation}
                title={translate("ShiftBoardCommentModal.clear_comment", "Clear comment")}
                accept_label={translate("common.clear", "Clear")}
                cancel_label={translate("common.cancel", "Cancel")}
                onAccept={handleClearConfirm}
                onCancel={handleClearCancel}
                dangerous={true}
            >
                <p className="mb-0">
                    {translate(
                        "ShiftBoardCommentModal.confirm_clear_comment",
                        "Are you sure you want to clear this comment?"
                    )}
                </p>
                {selectedSlotKeys.size > 0 && (
                    <p className="mb-0">
                        <strong>
                            {translate(
                                "ShiftBoardCommentModal.this_will_clear_comments_for_other_days_shifts_or_lines",
                                "This will also clear comments for other selected days, shifts or lines."
                            )}
                        </strong>
                    </p>
                )}
            </ConfirmationModal>
            <ConfirmationModal
                show={pendingDiscard !== null}
                title={translate("common.discard_unsaved_changes", "Discard unsaved changes?")}
                accept_label={translate("common.discard", "Discard")}
                cancel_label={translate("common.cancel", "Cancel")}
                onAccept={handleDiscardUnsavedConfirm}
                onCancel={handleDiscardUnsavedCancel}
                dangerous={true}
            >
                <p className="mb-0">
                    {translate(
                        "common.unsaved_changes_warning",
                        "You have unsaved changes. If you continue, they will be lost."
                    )}
                </p>
            </ConfirmationModal>
        </>
    );
};

export default ShiftBoardCommentModal;
